import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { statementPath } from './PartyStatement';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';

interface SupplierRow {
  id: string;
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  category: string | null;
  notes: string | null;
  paymentTermDays: number;
  isActive: boolean;
  outstanding: number;
  openBills: number;
  /** إجمالي فواتير المدى — null بلا مدى. */
  periodTotal: number | null;
}

interface SupplierPage {
  items: SupplierRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** على كل المطابقين لا الصفحة وحدها. */
  totals: { outstanding: number; openBills: number; periodTotal: number | null };
}

/** أعمدة الجدول بمفاتيحها — المفتاح نفسه مفتاح الفلتر في رأس العمود. */
const SUPPLIER_COLUMNS = [
  { key: 'name', label: 'المورد' },
  { key: 'category', label: 'التصنيف' },
  { key: 'phone', label: 'الهاتف' },
  { key: 'taxNumber', label: 'الرقم الضريبي' },
  { key: 'paymentTermDays', label: 'مهلة السداد' },
  { key: 'openBills', label: 'فواتير مفتوحة' },
  { key: 'outstanding', label: 'المستحق' },
  { key: 'isActive', label: 'الحالة' },
] as const;

/** عمود يظهر مع المدى وحده: إجمالي فواتير المورد الصادرة فيه. */
const PERIOD_COLUMN = { key: 'periodTotal', label: 'فواتير الفترة' } as const;

/**
 * أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في suppliers.service.ts.
 * «فواتير مفتوحة» و«المستحق» محسوبان من الفواتير بعد الجلب، فلا فلتر لهما.
 */
const SUPPLIER_FILTERS: FilterDef[] = [
  { key: 'name', label: 'المورد', kind: 'text', sortable: true },
  { key: 'category', label: 'التصنيف', kind: 'text' },
  { key: 'phone', label: 'الهاتف', kind: 'text' },
  { key: 'taxNumber', label: 'الرقم الضريبي', kind: 'text' },
  { key: 'paymentTermDays', label: 'مهلة السداد', kind: 'number', sortable: true },
];

export function SuppliersList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [editing, setEditing] = useState<SupplierRow | 'new' | null>(null);

  const from = state.from ?? '';
  const to = state.to ?? '';
  const ranged = !!(from || to);
  const canEdit = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };
  /* «فواتير الفترة» بعد «المستحق» مع المدى وحده. */
  const columns = ranged
    ? [...SUPPLIER_COLUMNS.slice(0, 7), PERIOD_COLUMN, ...SUPPLIER_COLUMNS.slice(7)]
    : SUPPLIER_COLUMNS;

  const list = useQuery({
    queryKey: ['suppliers', query],
    queryFn: () => api<SupplierPage>(`/finance/suppliers${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    setEditing(null);
  };

  const owed = list.data?.totals.outstanding ?? 0;

  /*
   * التصدير: كل الموردين المطابقين للبحث والفلاتر والمدى، لا الصفحة المعروضة
   * وحدها. وبلا مدى فالمستحق رصيدٌ كما في اليوم؛ ومعه من فواتير المدى وحدها.
   */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<SupplierRow>('/finance/suppliers', query, 100);
    return {
      title: 'الموردون',
      sheets: [
        {
          title: 'الموردون',
          period: ranged
            ? exportPeriod(from, to)
            : { label: `كما في ${exportDay(new Date().toISOString())}` },
          sections: [
            {
              columns: [
                { key: 'name', label: 'المورد' },
                { key: 'category', label: 'التصنيف' },
                { key: 'phone', label: 'الهاتف' },
                { key: 'taxNumber', label: 'الرقم الضريبي' },
                { key: 'paymentTermDays', label: 'مهلة السداد (يوم)', kind: 'number' },
                { key: 'openBills', label: 'فواتير مفتوحة', kind: 'number', total: true },
                { key: 'outstanding', label: 'المستحق', kind: 'money', total: true },
                ...(ranged
                  ? [{ key: 'periodTotal', label: 'فواتير الفترة', kind: 'money' as const, total: true }]
                  : []),
                { key: 'isActive', label: 'الحالة' },
              ],
              rows: items.map((s) => ({
                name: s.name,
                category: s.category,
                phone: s.phone,
                taxNumber: s.taxNumber,
                paymentTermDays: s.paymentTermDays,
                openBills: s.openBills,
                outstanding: s.outstanding,
                periodTotal: s.periodTotal,
                isActive: s.isActive ? 'نشط' : 'موقوف',
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: describeFilters(SUPPLIER_FILTERS, filters),
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="الموردون"
        subtitle={
          list.data
            ? `${plural(list.data.total, WORDS.supplier)}${owed > 0 ? ` — مستحق عليك ${money(owed)}` : ''}${
                ranged && list.data.totals.periodTotal
                  ? ` — فواتير الفترة ${money(list.data.totals.periodTotal)}`
                  : ''
              }`
            : undefined
        }
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={15} />
              مورد جديد
            </button>
          )
        }
      />

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الرقم الضريبي أو الهاتف"
        />

        {/* المورد بلا تاريخ: المدى مدى فواتيره، ومن لا فاتورة له فيه يُخفى. */}
        <DateRangeFilter from={from} to={to} onChange={(f, t) => patch({ from: f, to: t })} />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={SUPPLIER_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={SUPPLIER_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={SUPPLIER_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/suppliers/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                {canEdit && <th />}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={columns.length + (canEdit ? 1 : 0)} />}

            {list.data && (
              <tbody>
                {list.data.items.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-strong">{s.name}</td>
                    <td className="cell-mute">{s.category ?? '—'}</td>
                    <td className="num cell-mute" dir="ltr">
                      {s.phone ?? '—'}
                    </td>
                    <td className="num cell-mute">{s.taxNumber ?? '—'}</td>
                    <td className="cell-mute">{plural(s.paymentTermDays, WORDS.day)}</td>
                    <td className="num cell-mute">{s.openBills || '—'}</td>
                    <td className="num">
                      {s.outstanding > 0 ? (
                        <span className="neg">{money(s.outstanding)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    {ranged && <td className="num">{s.periodTotal ? money(s.periodTotal) : '—'}</td>}
                    <td>
                      <Badge kind={s.isActive ? 'success' : 'neutral'}>
                        {s.isActive ? 'نشط' : 'موقوف'}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td className="end" style={{ whiteSpace: 'nowrap' }}>
                        <Link
                          to={statementPath('supplier', s.id, { from: state.from, to: state.to })}
                          className="btn btn-sm btn-icon btn-ghost"
                          title={`كشف حساب ${s.name}`}
                          aria-label={`كشف حساب ${s.name}`}
                        >
                          <Icon name="receipt" size={15} />
                        </Link>
                        <Link
                          to={`/finance/recurring-bills?supplierId=${s.id}`}
                          className="btn btn-sm btn-icon btn-ghost"
                          title={`الاشتراكات الدورية لـ${s.name}`}
                          aria-label={`الاشتراكات الدورية لـ${s.name}`}
                        >
                          <Icon name="refresh" size={15} />
                        </Link>
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setEditing(s)}
                          aria-label={`تعديل ${s.name}`}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title={ranged ? 'لا موردين بفواتير في هذه الفترة' : 'لا يوجد موردون'}
            text="سجّل موردًا لتتمكن من تسجيل فواتيره ومصروفاته ومتابعة ما عليك له."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  مورد جديد
                </button>
              )
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {editing && (
        <SupplierModal
          supplier={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function SupplierModal({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: SupplierRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: supplier?.name ?? '',
    legalName: supplier?.legalName ?? '',
    taxNumber: supplier?.taxNumber ?? '',
    email: supplier?.email ?? '',
    phone: supplier?.phone ?? '',
    address: supplier?.address ?? '',
    category: supplier?.category ?? '',
    notes: supplier?.notes ?? '',
    paymentTermDays: String(supplier?.paymentTermDays ?? 30),
    isActive: supplier?.isActive ?? true,
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        legalName: form.legalName || undefined,
        taxNumber: form.taxNumber || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        category: form.category || undefined,
        notes: form.notes || undefined,
        paymentTermDays: Number(form.paymentTermDays) || 0,
        ...(supplier ? { isActive: form.isActive } : {}),
      };
      return supplier
        ? api(`/finance/suppliers/${supplier.id}`, { method: 'PATCH', body })
        : api('/finance/suppliers', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () => api<{ deactivated: boolean }>(`/finance/suppliers/${supplier!.id}`, {
      method: 'DELETE',
    }),
    onSuccess: onSaved,
  });

  return (
    <Modal
      title={supplier ? `تعديل ${supplier.name}` : 'مورد جديد'}
      onClose={onClose}
      footer={
        <>
          {supplier && (
            <button
              className="btn btn-danger me-auto"
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'جارٍ الحذف' : 'حذف'}
            </button>
          )}
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={form.name.trim().length < 2 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="sup-name">
            اسم المورد
          </label>
          <input
            id="sup-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="sup-legal">
            الاسم القانوني
          </label>
          <input
            id="sup-legal"
            className="input"
            value={form.legalName}
            onChange={(e) => set('legalName', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sup-tax">
            الرقم الضريبي
          </label>
          <input
            id="sup-tax"
            className="input num"
            value={form.taxNumber}
            onChange={(e) => set('taxNumber', e.target.value)}
            dir="ltr"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sup-category">
            التصنيف
          </label>
          <input
            id="sup-category"
            className="input"
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            placeholder="طباعة، إنتاج، خدمات مهنية"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sup-phone">
            الهاتف
          </label>
          <input
            id="sup-phone"
            className="input num"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            dir="ltr"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sup-email">
            البريد الإلكتروني
          </label>
          <input
            id="sup-email"
            className="input"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            dir="ltr"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sup-terms">
            مهلة السداد بالأيام
          </label>
          <input
            id="sup-terms"
            className="input num"
            type="number"
            min="0"
            value={form.paymentTermDays}
            onChange={(e) => set('paymentTermDays', e.target.value)}
          />
          <span className="tiny mute">
            تُحسب منها تواريخ استحقاق فواتيره تلقائيًا.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="sup-address">
            العنوان
          </label>
          <input
            id="sup-address"
            className="input"
            value={form.address}
            onChange={(e) => set('address', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="sup-notes">
            ملاحظات
          </label>
          <textarea
            id="sup-notes"
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>
      </div>

      {supplier && (
        <label className="row mt-4" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          <span className="small">نشط ويقبل تسجيل مستندات جديدة</span>
        </label>
      )}
    </Modal>
  );
}
